import type { AbsenceBlock, AbsenceFilterKind, PlantafelViewMode } from './plantafelTypes';

export type MonthViewportMeta = {
  viewportStart: Date;
  dayCount: number;
  weekCount: number;
  focusYear: number;
  focusMonth: number;
};

function getWeekEndSunday(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay();
  if (dow !== 0) d.setDate(d.getDate() + (7 - dow));
  return d;
}

/** Vollständiger Kalendermonat in ganzen Wochen (Mo der 1. bis So des letzten Tags). */
export function getMonthViewportMeta(focus: Date): MonthViewportMeta {
  const focusYear = focus.getFullYear();
  const focusMonth = focus.getMonth();
  const first = new Date(focusYear, focusMonth, 1);
  const last = new Date(focusYear, focusMonth + 1, 0);
  const viewportStart = getWeekStartMonday(first);
  const lastSunday = getWeekEndSunday(last);
  const dayCount = Math.round((lastSunday.getTime() - viewportStart.getTime()) / 86400000) + 1;
  const weekCount = dayCount / 7;
  return { viewportStart, dayCount, weekCount, focusYear, focusMonth };
}

export function getWeekStartMonday(date: Date): Date {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - day);
  return d;
}

export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function isWeekend(date: Date): boolean {
  const dow = date.getDay();
  return dow === 0 || dow === 6;
}

export function getViewportStart(focusDate: Date, viewMode: PlantafelViewMode): Date {
  if (viewMode === 'tag') return startOfDay(focusDate);
  if (viewMode === 'monat') return getMonthViewportMeta(focusDate).viewportStart;
  return getWeekStartMonday(focusDate);
}

export function getBoardRange(
  viewportStart: Date,
  viewMode: PlantafelViewMode,
  monthDayCount?: number,
): { from: Date; to: Date } {
  if (viewMode === 'tag') {
    // Inclusive same calendar day (column label); pair with ymdDayStart/EndIso.
    return { from: viewportStart, to: viewportStart };
  }
  if (viewMode === 'monat') {
    const days = monthDayCount ?? 35;
    return { from: viewportStart, to: addDays(viewportStart, days - 1) };
  }
  return { from: viewportStart, to: addDays(viewportStart, 6) };
}

export function addDays(base: Date, offset: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + offset);
  return d;
}

export function localDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function dayIndexInViewport(
  viewportStart: Date,
  instant: Date,
  viewMode: PlantafelViewMode,
  monthDayCount?: number,
): number {
  const vs = startOfDay(viewportStart);
  const t = startOfDay(instant);
  const diff = Math.round((t.getTime() - vs.getTime()) / 86400000);
  if (viewMode === 'tag') return diff === 0 ? 0 : -1;
  if (viewMode === 'monat') {
    const limit = monthDayCount ?? 35;
    return diff < 0 || diff >= limit ? -1 : diff;
  }
  return diff < 0 || diff > 6 ? -1 : diff;
}

export function isoWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

export function navigateFocusPrev(focusDate: Date, viewMode: PlantafelViewMode): Date {
  if (viewMode === 'tag') return addDays(focusDate, -1);
  if (viewMode === 'monat') return new Date(focusDate.getFullYear(), focusDate.getMonth() - 1, 1);
  return addDays(getWeekStartMonday(focusDate), -7);
}

export function navigateFocusNext(focusDate: Date, viewMode: PlantafelViewMode): Date {
  if (viewMode === 'tag') return addDays(focusDate, 1);
  if (viewMode === 'monat') return new Date(focusDate.getFullYear(), focusDate.getMonth() + 1, 1);
  return addDays(getWeekStartMonday(focusDate), 7);
}

export function getViewportDateKeys(
  viewportStart: Date,
  viewMode: PlantafelViewMode,
  monthDayCount?: number,
): { fromKey: string; toKey: string } {
  const { from, to } = getBoardRange(viewportStart, viewMode, monthDayCount);
  return { fromKey: localDateKey(from), toKey: localDateKey(to) };
}

export function employeeIdsWithAbsenceInRange(
  absences: AbsenceBlock[],
  kinds: AbsenceFilterKind[],
  fromKey: string,
  toKey: string,
): Set<string> {
  if (kinds.length === 0) return new Set();
  const allowed = new Set(kinds);
  const ids = new Set<string>();
  for (const abs of absences) {
    const kind = abs.absence_kind;
    if (kind === 'other' || kind === 'feiertag' || !allowed.has(kind)) continue;
    const d = String(abs.entry_date).slice(0, 10);
    if (d >= fromKey && d <= toKey) ids.add(abs.employee_id);
  }
  return ids;
}

export function rowCapacityHours(
  expectedHoursPerDay: number,
  weeklyCapacity: number,
  viewMode: PlantafelViewMode,
  monthDayCount?: number,
  visibleWeekdays?: number[],
): number {
  const visible = visibleWeekdays?.length ? visibleWeekdays : [0, 1, 2, 3, 4, 5, 6];
  if (viewMode === 'tag') return expectedHoursPerDay;
  if (viewMode === 'monat') {
    let count = 0;
    const days = monthDayCount ?? 35;
    for (let i = 0; i < days; i++) {
      if (visible.includes(i % 7)) count++;
    }
    return Math.round(count * expectedHoursPerDay * 10) / 10;
  }
  return weeklyCapacity;
}
